import { useEffect, useState } from "react";

import { useWsSubscribe } from "@/ws/WebSocketProvider";

interface Notification {
  id: number;
  time: number;
  type: "fill" | "alert" | "info";
  tone: "good" | "warn" | "bad" | "neutral";
  title: string;
  body?: string;
  read: boolean;
}

const STORAGE_KEY = "piggy.notifications.v1";
const MAX = 200;

let nextId = Date.now();

function load(): Notification[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Notification[];
  } catch {
    /* ignore */
  }
  return [];
}

function persist(items: Notification[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX)));
  } catch {
    /* ignore quota */
  }
}

/**
 * Bell icon + slide-in drawer. Persists fills/alerts to localStorage so
 * a refresh doesn't eat history. The unread badge clears on open.
 */
export function NotificationsCenter() {
  const [items, setItems] = useState<Notification[]>(() => load());
  const [open, setOpen] = useState(false);

  function add(n: Omit<Notification, "id" | "read">) {
    setItems((cur) => {
      const next = [{ ...n, id: ++nextId, read: false }, ...cur].slice(0, MAX);
      persist(next);
      return next;
    });
  }

  useWsSubscribe("fill", (msg) => {
    add({
      time: Date.now(),
      type: "fill",
      tone: msg.side === "BUY" ? "good" : "bad",
      title: `Fill: ${msg.side} ${msg.qty} ${msg.symbol}`,
      body: `@ ${msg.price}`,
    });
  });

  useWsSubscribe("alert", (msg) => {
    const level = String(msg.level ?? "info");
    add({
      time: Date.now(),
      type: "alert",
      tone: level === "error" ? "bad" : level === "warning" ? "warn" : level === "success" ? "good" : "neutral",
      title: String(msg.message ?? ""),
    });
  });

  useWsSubscribe("margin_event", (msg) => {
    const action = String(msg.action ?? "");
    const liq = Array.isArray(msg.liquidations) ? msg.liquidations.length : 0;
    add({
      time: Date.now(),
      type: "alert",
      tone: action === "STOP_OUT" ? "bad" : "warn",
      title: action === "STOP_OUT" ? `STOP-OUT (${liq} liquidations)` : "Margin call",
      body: `account ${String(msg.trading_account_id ?? "").slice(0, 8)}`,
    });
  });

  useWsSubscribe("price_alert", (msg) => {
    const dir = String(msg.condition ?? "").endsWith("ABOVE") ? "↑" : "↓";
    add({
      time: Date.now(),
      type: "alert",
      tone: "warn",
      title: `Alert: ${msg.symbol} ${dir} ${msg.threshold}`,
      body: `Fired at ${msg.fired_price} (${msg.side})`,
    });
  });

  // Clear unread when opening.
  useEffect(() => {
    if (!open) return;
    setItems((cur) => {
      const next = cur.map((n) => ({ ...n, read: true }));
      persist(next);
      return next;
    });
  }, [open]);

  function clearAll() {
    persist([]);
    setItems([]);
  }

  const unread = items.filter((n) => !n.read).length;

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative rounded-sm p-1 text-term-dim hover:bg-term-elev hover:text-term-fg"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M6 8a6 6 0 1 1 12 0c0 4 1.5 6 1.5 6h-15S6 12 6 8z" />
          <path d="M10 19a2 2 0 0 0 4 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute right-0 top-0 -mr-1 -mt-1 inline-flex min-w-[14px] items-center justify-center rounded-full bg-term-down px-1 text-[9px] font-bold text-white">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}>
          <div
            className="absolute right-0 top-0 h-full w-full max-w-sm bg-term-pane border-l border-term-border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-term-border px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-term-dim">
                Notifications
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={clearAll}
                  className="text-2xs text-term-mute hover:text-term-fg"
                  disabled={items.length === 0}
                >
                  Clear
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-sm p-1 text-term-mute hover:bg-term-elev hover:text-term-fg"
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 6l12 12M6 18L18 6" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="h-[calc(100%-40px)] overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-xs text-term-mute">
                  No notifications yet. Fills + engine alerts will land here.
                </div>
              ) : (
                items.map((n) => (
                  <div key={n.id} className="border-b border-term-border px-3 py-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className={`text-xs ${toneClass(n.tone)}`}>{n.title}</span>
                      <span className="text-2xs text-term-mute whitespace-nowrap">
                        {new Date(n.time).toLocaleTimeString()}
                      </span>
                    </div>
                    {n.body && <div className="mt-0.5 text-2xs text-term-dim">{n.body}</div>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function toneClass(tone: Notification["tone"]): string {
  switch (tone) {
    case "good": return "up";
    case "bad": return "down";
    case "warn": return "warn";
    case "neutral": return "text-term-fg";
  }
}
